// src/utils/markedSpreadsheetPages.js
// "Marked spreadsheet" pages for the marked PDF (FirebaseMarker.jsx's
// createAnnotatedPDF). Interactive spreadsheet marking
// (SpreadsheetCellViewer.jsx) puts marks, numbered comments, professional
// marks and highlights on cells instead of page positions - they carry
// `cell: { groupId, gridKey, row, col }` and no page, so the page-by-page
// drawing never shows them. Each spreadsheet is drawn here as a grid, with
// the marks in their cells and the comments down the same 200pt sidebar as
// every other marked page, laid out the same way (numbers restart on each
// page, professional marks interleave by P number, overflow goes onto a
// "(Feedback continued)" page). A cell's marking (comment numbers, circles,
// OFR/PAG/MAX stamps) goes in a strip under its figure - the row grows to
// make room - so it never covers anything the student typed.
//
// Where they go depends on the submission's spreadsheetMarkingMode (see
// utils/spreadsheetMarkingMode.js): 'spreadsheet' - every spreadsheet with
// anything in it, straight after the page with its "See Spreadsheet N" note
// (spreadsheetAnswers' pdfPage/blankPdfPage), since the PDF doesn't have the
// grid; otherwise only the spreadsheets that were marked, after the
// student's own pages. A grid too wide for a portrait page goes landscape,
// and one too wide even for that is split into bands of columns, with
// column A repeated on each so the rows can still be read.

import { rgb } from 'pdf-lib';
import { parseGrid, colLetter, effectiveAlign } from '../components/ExamSpreadsheetGrid';
import { evaluateGrid } from './spreadsheetFormulas';
import { gridHasContent } from './generateExamPdf';

const PORTRAIT = { w: 595.28, h: 841.89 }; // A4, same as the exam PDF's own pages
const LANDSCAPE = { w: 841.89, h: 595.28 };
const SIDEBAR = 200;
const MARGIN = 40;
const ROW_HEADER_W = 24;
const GRID_BOTTOM = 50;
const PORTRAIT_MIN_SCALE = 0.75; // narrower than this -> landscape
const LANDSCAPE_MIN_SCALE = 0.6; // and narrower than this -> bands of columns
const BAND_SCALE = 0.75;

const RED = rgb(0.91, 0.30, 0.24);
const FULL = rgb(0.106, 0.6, 0.545);
const HALF = rgb(0.267, 0.369, 0.576);
const PROF = rgb(0.95, 0.61, 0.07);
const PROF_ADDITIONAL = rgb(0.8, 0.2, 0.6);
const GRID_LINE = rgb(0.85, 0.87, 0.9);
const HEADER_FILL = rgb(0.95, 0.96, 0.97);
const GREY = rgb(0.45, 0.45, 0.45);

const hexToRgb = (hex) => rgb(
  parseInt(hex.substr(1, 2), 16) / 255,
  parseInt(hex.substr(3, 2), 16) / 255,
  parseInt(hex.substr(5, 2), 16) / 255
);

// Sidebar entries, laid out exactly like createAnnotatedPDF's
// renderFeedbackOnSidebar. Returns how many items fitted.
function drawSidebar(page, x0, items, startY, font, boldFont) {
  let y = startY;
  let drawn = 0;
  for (const item of items) {
    if (item.type === 'numbered') {
      const words = item.text.replace(/\n/g, ' ').split(' ');
      const lines = [];
      let line = '';
      words.forEach(word => {
        const test = line + (line ? ' ' : '') + word;
        if (test.length * 5.5 < SIDEBAR - 50) line = test;
        else { if (line) lines.push(line); line = word; }
      });
      if (line) lines.push(line);
      if (y - (lines.length * 12 + 35) < 50 && drawn > 0) break;

      const numberText = String(item.number);
      const numberWidth = boldFont.widthOfTextAtSize(numberText, 14);
      const firstX = Math.max(x0 + 30, x0 + 15 + numberWidth + 6);
      page.drawText(numberText, { x: x0 + 15, y, size: 14, font: boldFont, color: RED });
      lines.forEach((l, i) => {
        const lx = i === 0 ? firstX : x0 + 30;
        page.drawText(l, { x: lx, y: y - i * 12, size: 9, font, color: rgb(0, 0, 0), maxWidth: SIDEBAR - (lx - x0) - 10 });
      });
      y -= lines.length * 12 + 25;
      if (y > 50) {
        page.drawLine({ start: { x: x0 + 10, y: y + 10 }, end: { x: x0 + SIDEBAR - 10, y: y + 10 }, thickness: 1, color: rgb(0.9, 0.9, 0.9) });
      }
    } else {
      const words = item.reason.split(' ');
      const lines = [];
      let line = '';
      words.forEach(word => {
        const test = line + (line ? ' ' : '') + word;
        if (test.length * 4 < SIDEBAR - 50) line = test;
        else { if (line) lines.push(line); line = word; }
      });
      if (line) lines.push(line);
      if (y - (lines.length * 11 + 30) < 50 && drawn > 0) break;

      const labelText = `${item.label}:`;
      const labelWidth = boldFont.widthOfTextAtSize(labelText, 10);
      const firstX = Math.max(x0 + 30, x0 + 15 + labelWidth + 6);
      page.drawText(labelText, { x: x0 + 15, y, size: 10, font: boldFont, color: item.color });
      lines.forEach((l, i) => {
        const lx = i === 0 ? firstX : x0 + 30;
        page.drawText(l, { x: lx, y: y - i * 11, size: 8, font, color: rgb(0.3, 0.3, 0.3), maxWidth: SIDEBAR - (lx - x0) - 10 });
      });
      y -= lines.length * 11 + 20;
    }
    drawn++;
  }
  return drawn;
}

// A new page with the sidebar, just before `beforePage` (one of the
// student's own pages, wherever earlier pages have since been inserted
// around it) or at the end
function newPage(pdfDoc, size, beforePage) {
  const dims = [size.w + SIDEBAR, size.h];
  const at = beforePage ? pdfDoc.getPages().findIndex(p => p.ref === beforePage.ref) : -1;
  const page = at >= 0 ? pdfDoc.insertPage(at, dims) : pdfDoc.addPage(dims);
  page.drawRectangle({ x: 0, y: 0, width: size.w + SIDEBAR, height: size.h, color: rgb(1, 1, 1) });
  page.drawLine({ start: { x: size.w, y: 0 }, end: { x: size.w, y: size.h }, thickness: 2, color: rgb(0.8, 0.8, 0.8) });
  return page;
}

// Page size, scale and column bands for a grid of these widths (px)
function chooseLayout(pxWidths) {
  const fit = (size) => size.w - 2 * MARGIN - ROW_HEADER_W;
  const width = (cols) => cols.reduce((sum, c) => sum + pxWidths[c] * 0.75, 0);
  const all = pxWidths.map((_, c) => c);
  const total = width(all);
  if (total * PORTRAIT_MIN_SCALE <= fit(PORTRAIT)) return { size: PORTRAIT, scale: Math.min(1, fit(PORTRAIT) / total), bands: [all] };
  if (total * LANDSCAPE_MIN_SCALE <= fit(LANDSCAPE)) return { size: LANDSCAPE, scale: Math.min(1, fit(LANDSCAPE) / total), bands: [all] };
  const room = fit(LANDSCAPE) / BAND_SCALE;
  const bands = [];
  let band = [];
  for (let c = 0; c < pxWidths.length; c++) {
    if (band.length > (bands.length ? 1 : 0) && width([...band, c]) > room) {
      bands.push(band);
      band = [0];
    }
    band.push(c);
  }
  bands.push(band);
  return { size: LANDSCAPE, scale: Math.min(1, ...bands.map(b => room * BAND_SCALE / width(b))), bands };
}

const columnsLabel = (cols) => {
  const parts = [];
  let i = 0;
  while (i < cols.length) {
    let j = i;
    while (j + 1 < cols.length && cols[j + 1] === cols[j] + 1) j++;
    parts.push(j > i ? `${colLetter(cols[i])} to ${colLetter(cols[j])}` : colLetter(cols[i]));
    i = j + 1;
  }
  return parts.join(', ');
};

// Text cut down to a width (numbers shrink instead, then show ### like a
// spreadsheet - a cut-off number would read as a different number)
function fitText(f, text, size, maxW, numeric) {
  if (f.widthOfTextAtSize(text, size) <= maxW) return { text, size };
  if (numeric) {
    for (let s = size - 0.5; s >= 4; s -= 0.5) {
      if (f.widthOfTextAtSize(text, s) <= maxW) return { text, size: s };
    }
    return { text: '###', size };
  }
  let t = text;
  while (t.length > 1 && f.widthOfTextAtSize(`${t}...`, size) > maxW) t = t.slice(0, -1);
  return { text: `${t}...`, size };
}

// A cell's marks as boxes in lines, each line no wider than the cell
function layoutMarks(tokens, maxW, gap) {
  const lines = [];
  let line = [];
  let w = 0;
  tokens.forEach(t => {
    if (line.length && w + gap + t.w > maxW) { lines.push({ tokens: line, w }); line = []; w = 0; }
    w += (line.length ? gap : 0) + t.w;
    line.push(t);
  });
  if (line.length) lines.push({ tokens: line, w });
  return lines;
}

export async function addMarkedSpreadsheetPages({ pdfDoc, data, spreadsheetAnswers, mode, originalPages = [], font, boldFont }) {
  if (!spreadsheetAnswers) return;
  const all = {
    marks: (data.marks || []).filter(m => m.cell),
    feedback: (data.numberedFeedback || []).filter(f => f.cell && f.text && f.text.trim()),
    prof: [
      ...(data.professionalMarks || []).filter(m => m.cell).map(m => ({ ...m, color: PROF })),
      ...(data.additionalProfessionalMarks || []).filter(m => m.cell).map(m => ({ ...m, color: PROF_ADDITIONAL }))
    ],
    highlights: (data.annotations || []).filter(a => a.type === 'highlight' && a.cell),
    stamps: (data.textStamps || []).filter(t => t.cell)
  };
  const inPlace = mode === 'spreadsheet';

  // Every grid, in Spreadsheet 1, 2... order
  const sheets = [];
  Object.entries(spreadsheetAnswers).forEach(([groupId, group]) => {
    [['spreadsheet', group.gridJson, group.number, group.pdfPage], ['blankSpreadsheet', group.blankGridJson, group.blankNumber, group.blankPdfPage]].forEach(([gridKey, gridJson, number, pdfPage]) => {
      if (gridJson) sheets.push({ groupId, group, gridKey, gridJson, number, pdfPage });
    });
  });
  sheets.sort((a, b) => (a.number || Infinity) - (b.number || Infinity));

  for (const { groupId, group, gridKey, gridJson, number, pdfPage } of sheets) {
    const here = (c) => c.groupId === groupId && c.gridKey === gridKey;
    const marks = all.marks.filter(m => here(m.cell));
    const feedback = all.feedback.filter(f => here(f.cell));
    const prof = all.prof.filter(m => here(m.cell));
    const highlights = all.highlights.filter(a => here(a.cell));
    const stamps = all.stamps.filter(t => here(t.cell));
    const marked = marks.length || feedback.length || prof.length || highlights.length || stamps.length;
    if (!marked && !(inPlace && gridHasContent(gridJson))) continue;
    // Straight after the page with the "See Spreadsheet N" note - i.e. before
    // the student's next page - or after all of them
    const beforePage = inPlace && pdfPage >= 1 ? originalPages[pdfPage] : undefined;

    const { grid, colWidths: studentWidths } = parseGrid(gridJson);
    // Widths the marker set while marking, if any
    const markerWidths = data.spreadsheetColWidths?.[`${groupId}:${gridKey}`];
    const colWidths = Array.isArray(markerWidths) && markerWidths.length === studentWidths.length ? markerWidths : studentWidths;
    let evaluated = null;
    if (gridKey === 'spreadsheet' && group.precomputedEvalJson) {
      try { evaluated = JSON.parse(group.precomputedEvalJson); } catch (e) { evaluated = null; }
    }
    if (!evaluated) evaluated = evaluateGrid(grid);
    const display = (r, c) => String(evaluated[r]?.[c]?.display ?? '');

    // Used range: anything typed, plus anything marked
    let lastRow = 0;
    let lastCol = 0;
    grid.forEach((row, r) => row.forEach((cell, c) => {
      if (display(r, c).trim() || String(cell?.value ?? '').trim()) { lastRow = Math.max(lastRow, r); lastCol = Math.max(lastCol, c); }
    }));
    [...marks, ...feedback, ...prof, ...highlights, ...stamps].forEach(i => { lastRow = Math.max(lastRow, i.cell.row); lastCol = Math.max(lastCol, i.cell.col); });
    lastRow = Math.min(lastRow, grid.length - 1);
    lastCol = Math.min(lastCol, (grid[0]?.length || 1) - 1);

    // Column widths from the student's own, shrunk to fit the page
    const pxWidths = Array.from({ length: lastCol + 1 }, (_, c) => colWidths[c] || 90);
    const { size, scale, bands } = chooseLayout(pxWidths);
    const PAGE_W = size.w;
    const PAGE_H = size.h;
    const GRID_TOP = PAGE_H - 96;
    const fontSize = Math.max(5, 8 * scale);
    const rowH = Math.max(10, 15 * scale);
    const R = Math.min(6.5, rowH / 2 - 0.5);
    const GAP = 1.5;
    const LINE_H = 2 * R + 2;
    const nSize = Math.min(9, rowH - 2);
    const stampSize = Math.min(8, rowH - 5);

    const name = group.name || groupId;
    const which = gridKey === 'blankSpreadsheet' ? 'blank spreadsheet' : group.blankGridJson ? 'prepopulated spreadsheet' : 'spreadsheet';
    const heading = number ? `Spreadsheet ${number} (${name})` : name;

    for (let bandIndex = 0; bandIndex < bands.length; bandIndex++) {
      const cols = bands[bandIndex];
      const inBand = new Set(cols);
      const colW = {};
      const colX = {};
      cols.reduce((x, c) => { colW[c] = pxWidths[c] * 0.75 * scale; colX[c] = x; return x + colW[c]; }, MARGIN + ROW_HEADER_W);

      // Everything marked on a cell, in the order it's drawn. `numberOf` gives
      // a comment's number - its own until a page renumbers it (never wider).
      const cellTokens = (r, c, numberOf) => {
        const at = (i) => i.cell.row === r && i.cell.col === c;
        return [
          ...feedback.filter(at).sort((a, b) => a.number - b.number).map(f => {
            const label = String(numberOf(f));
            return { kind: 'number', label, w: boldFont.widthOfTextAtSize(label, nSize) + 1 };
          }),
          ...marks.filter(at).sort((a, b) => a.id - b.id).map(m => {
            const full = m.type === 'full-technical';
            return { kind: 'circle', fill: full ? FULL : HALF, label: full ? '1' : '½', labelSize: R * 1.15, w: 2 * R };
          }),
          ...prof.filter(at).map(m => ({ kind: 'circle', fill: m.color, label: `P${m.markNumber || '?'}`, labelSize: R * 0.85, w: 2 * R })),
          ...stamps.filter(at).map(t => ({ kind: 'stamp', label: t.label, w: boldFont.widthOfTextAtSize(t.label, stampSize) + 4 }))
        ];
      };
      // Column A repeats on every band after the first, but its marking
      // only shows (and counts) once, on the first
      const marksShownIn = (c) => bandIndex === 0 || c !== 0;
      const stripLines = (r, c, numberOf) => (marksShownIn(c) ? layoutMarks(cellTokens(r, c, numberOf), colW[c] - 4, GAP) : []);
      const rowHeight = Array.from({ length: lastRow + 1 }, (_, r) => {
        let lines = 0;
        cols.forEach(c => { lines = Math.max(lines, stripLines(r, c, f => f.number).length); });
        return rowH + (lines ? lines * LINE_H + 1 : 0);
      });

      // Rows per page by height - marked rows are taller. Filled as far as
      // they go to find how many pages it takes, then spread evenly over
      // that many, so the last page isn't left with a row or two.
      const room = GRID_TOP - rowH - GRID_BOTTOM;
      const paginate = (limit) => {
        const result = [];
        for (let r = 0; r <= lastRow;) {
          let used = 0;
          const first = r;
          while (r <= lastRow && (r === first || used + rowHeight[r] <= limit)) used += rowHeight[r++];
          result.push([first, r - 1]);
        }
        return result;
      };
      let pages = paginate(room);
      if (pages.length > 1) {
        const total = rowHeight.reduce((a, b) => a + b, 0);
        const even = paginate(Math.min(room, Math.ceil(total / pages.length) + rowH));
        if (even.length === pages.length) pages = even;
      }

      for (let part = 0; part < pages.length; part++) {
        const [r0, r1] = pages[part];
        const page = newPage(pdfDoc, size, beforePage);
        const continued = part > 0 || bandIndex > 0;
        const title = fitText(boldFont, `${heading}: your ${which}, marked${continued ? ' (continued)' : ''}`, 14, PAGE_W - 2 * MARGIN, true);
        page.drawText(title.text, { x: MARGIN, y: PAGE_H - 50, size: title.size, font: boldFont, color: rgb(0.17, 0.24, 0.31) });
        const subtitle = [
          inPlace ? 'Your spreadsheet with its marking.' : 'Your spreadsheet with its marking. It is also earlier in this document as you handed it in.',
          bands.length > 1 ? `Too wide for one page: this page has columns ${columnsLabel(cols)}.` : ''
        ].filter(Boolean).join(' ');
        page.drawText(subtitle, { x: MARGIN, y: PAGE_H - 68, size: 9, font, color: GREY, maxWidth: PAGE_W - 2 * MARGIN });

        // Column letters
        const headY = GRID_TOP - rowH;
        page.drawRectangle({ x: MARGIN, y: headY, width: ROW_HEADER_W, height: rowH, color: HEADER_FILL, borderColor: GRID_LINE, borderWidth: 0.5 });
        cols.forEach(c => {
          page.drawRectangle({ x: colX[c], y: headY, width: colW[c], height: rowH, color: HEADER_FILL, borderColor: GRID_LINE, borderWidth: 0.5 });
          const letter = colLetter(c);
          page.drawText(letter, { x: colX[c] + (colW[c] - font.widthOfTextAtSize(letter, fontSize)) / 2, y: headY + (rowH - fontSize) / 2 + 1, size: fontSize, font, color: GREY });
        });

        // Top edge of each row on this page
        const rowTop = {};
        for (let r = r0, y = headY; r <= r1; y -= rowHeight[r], r++) rowTop[r] = y;
        const onPage = (i) => i.cell.row >= r0 && i.cell.row <= r1 && inBand.has(i.cell.col) && marksShownIn(i.cell.col);

        // Comments on this page, numbered from 1 like every other marked page
        const pageFeedback = feedback.filter(onPage).sort((a, b) => a.number - b.number);
        const shownNumber = new Map(pageFeedback.map((f, i) => [f.id, i + 1]));

        for (let r = r0; r <= r1; r++) {
          const h = rowHeight[r];
          const y = rowTop[r] - h; // bottom edge
          const textY = rowTop[r] - rowH; // bottom of the figure's own line
          page.drawRectangle({ x: MARGIN, y, width: ROW_HEADER_W, height: h, color: HEADER_FILL, borderColor: GRID_LINE, borderWidth: 0.5 });
          const rowLabel = String(r + 1);
          page.drawText(rowLabel, { x: MARGIN + (ROW_HEADER_W - font.widthOfTextAtSize(rowLabel, fontSize)) / 2, y: textY + (rowH - fontSize) / 2 + 1, size: fontSize, font, color: GREY });

          cols.forEach(c => {
            const hl = highlights.find(a => a.cell.row === r && a.cell.col === c);
            page.drawRectangle({ x: colX[c], y, width: colW[c], height: h, color: rgb(1, 1, 1), borderColor: GRID_LINE, borderWidth: 0.5 });
            if (hl) page.drawRectangle({ x: colX[c], y, width: colW[c], height: h, color: hexToRgb(hl.color), opacity: 0.3 });
          });

          cols.forEach((c, ci) => {
            const text = display(r, c);
            if (!text.trim()) return;
            const cell = grid[r]?.[c] || {};
            const cellEval = evaluated[r]?.[c];
            const numeric = cellEval && !cellEval.isError && cellEval.numeric !== null && cellEval.numeric !== undefined;
            const align = effectiveAlign(cell, cellEval);
            const f = cell.bold ? boldFont : font;
            // Left-aligned text runs on into empty cells to its right, as in a spreadsheet
            let textRoom = colW[c] - 4;
            if (align === 'left' && !numeric) {
              for (let n = ci + 1; n < cols.length && cols[n] === cols[n - 1] + 1 && !display(r, cols[n]).trim(); n++) textRoom += colW[cols[n]];
            }
            const fitted = fitText(f, text, fontSize, textRoom, numeric);
            const w = f.widthOfTextAtSize(fitted.text, fitted.size);
            const x = align === 'right' ? colX[c] + colW[c] - 2 - w
              : align === 'center' ? colX[c] + (colW[c] - w) / 2
              : colX[c] + 2;
            page.drawText(fitted.text, { x, y: textY + (rowH - fitted.size) / 2 + 1, size: fitted.size, font: f, color: cellEval?.isError ? RED : rgb(0, 0, 0) });
          });

          // The cell's marking, right-aligned in the strip under its figure
          cols.forEach(c => {
            stripLines(r, c, fb => shownNumber.get(fb.id)).forEach((line, li) => {
              const cy = textY - li * LINE_H - LINE_H / 2;
              let x = colX[c] + colW[c] - 2 - line.w;
              line.tokens.forEach(t => {
                if (t.kind === 'number') {
                  page.drawText(t.label, { x, y: cy - nSize * 0.36, size: nSize, font: boldFont, color: RED });
                } else if (t.kind === 'circle') {
                  page.drawCircle({ x: x + R, y: cy, size: R, color: t.fill, borderWidth: 1, borderColor: rgb(1, 1, 1) });
                  page.drawText(t.label, { x: x + R - boldFont.widthOfTextAtSize(t.label, t.labelSize) / 2, y: cy - t.labelSize * 0.36, size: t.labelSize, font: boldFont, color: rgb(1, 1, 1) });
                } else {
                  const sy = cy - (stampSize + 3) / 2;
                  page.drawRectangle({ x, y: sy, width: t.w, height: stampSize + 3, color: rgb(1, 1, 1), borderWidth: 1, borderColor: rgb(0.102, 0.102, 0.180) });
                  page.drawText(t.label, { x: x + 2, y: sy + 2, size: stampSize, font: boldFont, color: rgb(0.102, 0.102, 0.180) });
                }
                x += t.w + GAP;
              });
            });
          });
        }

        // Comments and professional mark reasons down the sidebar
        let items = [
          ...pageFeedback.map(f => ({ type: 'numbered', sortKey: f.number, number: shownNumber.get(f.id), text: f.text })),
          ...prof.filter(onPage).filter(m => m.reason && m.reason.trim()).map(m => ({ type: 'professional', sortKey: m.markNumber || 999, label: `P${m.markNumber || '?'}`, reason: m.reason, color: m.color }))
        ].sort((a, b) => a.sortKey - b.sortKey);
        let target = page;
        while (items.length) {
          const drawn = drawSidebar(target, PAGE_W, items, PAGE_H - 30, font, boldFont);
          items = items.slice(drawn);
          if (!items.length) break;
          target = newPage(pdfDoc, size, beforePage);
          target.drawText('(Feedback continued)', { x: 50, y: PAGE_H - 50, size: 12, font: boldFont, color: rgb(0.5, 0.5, 0.5) });
        }
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  }
}
